import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SafeImage from "@/components/base/SafeImage";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { formatDate, getNewsById, relatedNews, shortDate } from "@/lib/catalog";

export default function NewsDetailPage() {
  const { newsId } = useParams<{ newsId: string }>();
  const item = getNewsById(newsId);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timeout = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timeout);
  }, [copied]);

  useDocumentMeta({
    title: item ? `${item.title} - 日本情色电影资讯` : "资讯不存在 - 日本情色电影",
    description: item
      ? `${item.excerpt.slice(0, 90)} 本文由${item.author}撰写，分类：${item.category}，发布于 ${item.date}。`
      : "该篇资讯不存在或已调整，请返回资讯列表浏览其他文章。",
    keywords: item ? [item.category, ...item.tags, "日本电影资讯", "日本情色电影"].join(",") : "日本电影资讯",
  });

  const sides = useMemo(() => (item ? relatedNews(item, 3) : []), [item]);

  if (!item) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-24">
          <div className="w-full max-w-lg rounded-lg border border-background-300/60 bg-background-100 p-8 text-center">
            <i className="ri-article-line text-3xl text-accent-500" />
            <h1 className="mt-4 font-heading text-xl font-bold text-foreground-50">找不到这篇资讯</h1>
            <p className="mt-2 text-[13px] leading-relaxed text-foreground-400">
              文章可能已被调整。你可以回到资讯列表，浏览全部动态与影评。
            </p>
            <Link
              to="/news"
              className="mt-6 inline-flex cursor-pointer items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-[13px] text-foreground-50 transition-colors hover:bg-primary-600 font-label"
            >
              返回资讯列表
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <article className="mx-auto w-full max-w-[900px] px-4 py-8 md:px-8 md:py-12">
          <Breadcrumb items={[{ label: "资讯", to: "/news" }, { label: item.category, to: "/news" }]} />

          <header className="mt-6">
            <p className="flex flex-wrap items-center gap-2 text-[11px] tracking-widest2 text-accent-500 font-label">
              <span className="rounded-sm bg-primary-900/40 px-2 py-0.5">{item.category}</span>
              {item.tags.map((tag) => (
                <span key={tag} className="text-foreground-600">
                  #{tag}
                </span>
              ))}
            </p>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-50 md:text-4xl">
              {item.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-background-300/50 pb-5 text-[12px] text-foreground-500 font-label">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-accent-500/80" />
                {formatDate(item.date)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line text-accent-500/80" />
                {item.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-accent-500/80" />
                约 {Math.max(3, Math.round(item.sections.map((s) => s.text.length).join("").length / 320))} 分钟阅读
              </span>
              <button
                type="button"
                onClick={copyLink}
                className="ml-auto flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-foreground-400 transition-colors hover:text-accent-400"
              >
                <i className={copied ? "ri-check-line text-accent-400" : "ri-links-line"} />
                {copied ? "链接已复制" : "复制链接"}
              </button>
            </div>
          </header>

          <div className="mt-6 w-full overflow-hidden rounded-lg border border-background-300/60 bg-background-200">
            <div className="aspect-[16/9] w-full">
              <SafeImage
                src={item.cover}
                alt={`${item.title} 配图`}
                title={`${item.title} - 日本情色电影资讯`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <p className="mt-6 border-l-2 border-accent-500/70 pl-4 text-[13px] leading-relaxed text-foreground-300 md:text-sm">
            {item.excerpt}
          </p>

          <div className="mt-8 flex flex-col gap-6">
            {item.sections.map((section, index) => {
              const image = "image" in section ? section.image : undefined;
              const caption = "caption" in section ? section.caption : undefined;
              return (
                <section key={index}>
                  <p className="text-[13px] leading-[1.95] text-foreground-300 md:text-[14px]">{section.text}</p>
                  {image && (
                    <figure className="mt-5">
                      <div className="w-full overflow-hidden rounded-lg border border-background-300/50 bg-background-200">
                        <div className="aspect-[16/10] w-full">
                          <SafeImage
                            src={image}
                            alt={caption || `${item.title} 配图 ${index + 1}`}
                            title={`${item.title} - 日本情色电影资讯配图`}
                            className="h-full w-full object-cover object-top"
                          />
                        </div>
                      </div>
                      {caption && (
                        <figcaption className="mt-2 text-[11px] text-foreground-600 font-label">
                          {caption}（示意配图）
                        </figcaption>
                      )}
                    </figure>
                  )}
                </section>
              );
            })}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-background-300/50 pt-6">
            <Link
              to="/news"
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300/80 px-4 py-2 text-[12px] text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400 font-label"
            >
              <i className="ri-arrow-left-line" />
              返回资讯列表
            </Link>
            <p className="text-[11px] text-foreground-600 font-label">
              本文为演示虚构内容 · 作者与事件均非真实
            </p>
          </div>
        </article>

        <section className="w-full border-t border-background-300/50 bg-background-100/50">
          <div className="mx-auto w-full max-w-[1100px] px-4 py-10 md:px-8 md:py-14">
            <h2 className="font-heading text-lg font-bold text-foreground-50">相关资讯</h2>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
              {sides.map((side) => (
                <Link
                  key={side.id}
                  to={`/news/detail/${side.id}`}
                  className="group flex cursor-pointer flex-col rounded-lg border border-background-300/60 bg-background-50 p-4 transition-colors hover:border-accent-500/60"
                >
                  <p className="text-[10px] tracking-widest2 text-accent-500 font-label">{side.category}</p>
                  <h3 className="mt-2 line-clamp-2 font-heading text-[13px] font-medium leading-snug text-foreground-100 transition-colors group-hover:text-accent-400">
                    {side.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-[11px] leading-relaxed text-foreground-500">{side.excerpt}</p>
                  <span className="mt-3 font-display text-[12px] text-foreground-600">{shortDate(side.date)}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <div className="mx-auto w-full max-w-[1100px] px-4 pb-12 md:px-8">
          <div className="flex items-center gap-3 rounded-lg border border-background-300/50 bg-background-100/40 px-4 py-3 text-[11px] text-foreground-500 font-label">
            <i className="ri-information-line text-accent-500" />
            本站资讯均为演示内容，不构成任何作品推荐、版权主张或商业建议。
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}